<template>
  <div
    v-if="visible"
    class="banner"
    data-test-id="banner"
    :style="cssVars"
  >
    <a
      v-safe-html="notificationText"
      class="banner__content"
      data-test-id="banner-content"
      target="_blank"
      :href="notification.target_url"
      rel="noopener"
      @click="onClick"
    />
    <EIconButton
      :style="closeStyle"
      class="banner__close"
      data-test-id="banner-close"
      icon="close"
      aria-label="Close"
      @click="onClose"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { LAST_DISCARDED_NOTIFICATION as LAST_DISCARDED_NOTIFICATION_COOKIE } from '~~/common/js/constants/cookies';
import { sendEvent } from '~~/common/js/client/analytics';
import EIconButton from '~~/common/vue/components/icon-button';
import { getCookieOptions, isDateInLastDays } from '~~/common/js/universal/utils';
import { NOTIFICATION_DAYS_BANNER, NOTIFICATION_LEVEL_BANNER } from './constants';

export default {
  components: {
    EIconButton,
  },
  props: {
    backgroundL: {
      type: String,
      default: '',
    },
    backgroundM: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      impressionEventSent: false,
    };
  },
  computed: {
    ...mapState('user', [
      'lastDiscardedNotification',
      'notificationEnabled',
    ]),
    ...mapState('notifications', {
      visible: 'bannerVisible',
    }),
    notification() {
      return this.$store.getters['notifications/getForLevel'](NOTIFICATION_LEVEL_BANNER);
    },
    notificationText() {
      return this.notification.text;
    },
    // FIXME: stopgap measure for Nuxt 2.3.4 webpack plugin messing up the order of inlined CSS
    // see: https://github.com/nuxt/nuxt.js/issues/4219#issuecomment-466472392
    closeStyle() {
      return { padding: 0 };
    },
    cssVars() {
      return this.backgroundL && this.backgroundM ? {
        '--banner-background-m': `url(${this.backgroundM})`,
        '--banner-background-l': `url(${this.backgroundL})`,
      } : null;
    },
  },
  watch: {
    notification: {
      handler: 'setVisibility',
      immediate: true,
    },
  },
  mounted() {
    this.unwatch = this.$watch('visible', this.sendImpressionEvent.bind(this), { immediate: true });
  },
  beforeDestroy() {
    if (this.unwatch) {
      this.unwatch();
    }
  },
  methods: {
    sendEvent(name) {
      sendEvent(name, {
        origin: 'banner',
        level: NOTIFICATION_LEVEL_BANNER,
        trackingname: this.notification.tracking_name,
      });
    },
    sendImpressionEvent(visible) {
      if (visible && !this.impressionEventSent) {
        this.sendEvent('all_notification_impression');
        this.impressionEventSent = true;
      }
    },
    setVisibility(notification) {
      if (!notification) {
        return;
      }
      const publishDate = new Date(notification.publish_date).getTime();
      const publishedRecently = isDateInLastDays(publishDate, NOTIFICATION_DAYS_BANNER);
      const neverSeen = (
        !this.lastDiscardedNotification ||
        (
          this.lastDiscardedNotification &&
          new Date(this.lastDiscardedNotification.banner) < publishDate
        )
      );

      if (
        this.notificationEnabled &&
        publishedRecently &&
        neverSeen
      ) {
        this.$store.commit('notifications/setBannerVisible', true);
      }
    },
    sendClickEvent() {
      this.sendEvent('all_notification_click');
    },
    sendDismissEvent() {
      this.sendEvent('all_notification_dismiss');
    },
    storeLastDiscarded() {
      const key = LAST_DISCARDED_NOTIFICATION_COOKIE;
      const value = {
        banner: Date.now(),
        popup: this.lastDiscarded ? this.lastDiscarded.popup : null,
      };
      const options = getCookieOptions();
      this.$cookies.set(key, value, options);
    },
    hide() {
      this.$store.commit('notifications/setBannerVisible', false);
      this.storeLastDiscarded();
    },
    onClick() {
      this.hide();
      this.sendClickEvent();
    },
    onClose() {
      this.hide();
      this.sendDismissEvent();
    },
  },
};
</script>

<style lang="scss" scoped>
$close-size: $space-2l + $space-2s;
$notification-banner-height: $space-4l;

.banner {
  position: relative;
  display: flex;
  align-items: center;
  background-color: $gray-10;
  min-height: $notification-banner-height;

  &::before,
  &::after {
    height: 100%;
    max-width: 396px;
    min-height: $space-4l;
    flex-shrink: 0;
    width: $space-4l + $space-2l;
    background-position: center;
    content: '';
    background-image: var(--banner-background-m);
    background-repeat: no-repeat;

    @include desktop {
      height: $notification-banner-height;
      flex: 0 1 auto;
      display: block;
      width: 100%;
      background-image: var(--banner-background-l);
      background-position: bottom right;
    }
  }

  &::after {
    display: none;
    transform: scaleX(-1);

    @include desktop {
      display: block;
    }
  }
}

.banner__content {
  flex-grow: 0;
  flex-shrink: 1;
  margin: $space-2s 0;
  padding: $space-s $space-4l + $space-2l $space-s $space-s;
  font-size: $font-s;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
  color: $color-text-primary;
  line-height: $space-m + 3px;

  @include desktop {
    font-size: $font-1l;
    flex-grow: 2;
    padding: 0 $space-4l;
    line-height: $space-4l;
    font-weight: $font-weight-1l;
    white-space: nowrap;
    text-align: center;
  }

  &:focus,
  &:hover {
    text-decoration: underline;
  }
}

.banner__close {
  position: absolute;
  right: $space-2l;
  width: $close-size;
  height: $close-size;
  line-height: $close-size;
  z-index: $z-index-l;
  border-radius: $border-radius-full;
  background-color: rgba($white, $opacity-1l);

  &:focus,
  &:hover {
    background-color: $white;
  }
}
</style>
